import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ChevronRight, FileText } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function TermsOfService() {
    return (
        <LandingLayout>
            <SeoHead
                title="Terms of Service"
                description="Terms of service for Northeastern College's website."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section id="terms-of-service" className="bg-muted pb-16">
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/terms.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Terms of Service</span>
                        </div>
                        
                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Terms of Service
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Review the guidelines for using the official Northeastern College website and online services.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="bg-card border-border overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-10">
                        <div className="mb-8 rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-5 py-4">
                            <p className="text-sm font-semibold tracking-wide text-[#0A7A35] uppercase">Last Updated</p>
                            <p className="text-foreground mt-1 text-lg font-bold">January 24, 2026</p>
                        </div>
                        <p className="text-muted-foreground mb-6 text-sm leading-relaxed md:text-base">
                            These Terms of Service govern your use of the Northeastern College website. By accessing or using the site, you agree to
                            these terms.
                        </p>

                        <div className="text-muted-foreground space-y-6 text-sm leading-relaxed md:text-base">
                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">1. Acceptance of Terms</h3>
                                <div>
                                    By using our website, you agree to follow all policies and terms included here. If you do not agree, please
                                    discontinue use of the website.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">2. Permitted Use</h3>
                                <div className="mb-2">The website is intended for:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Students</li>
                                    <li>Parents and guardians</li>
                                    <li>Prospective students</li>
                                    <li>Staff, partners, and the general public</li>
                                </ul>
                                <div className="mt-3 mb-2">You agree not to:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Engage in illegal or fraudulent activities</li>
                                    <li>Attempt unauthorized access to administrative systems</li>
                                    <li>Disrupt or damage the website</li>
                                    <li>Upload malicious files or scripts</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">3. Account Access</h3>
                                <div>Only authorized administrators have login accounts.</div>
                                <div>Users do not create accounts on the public site.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">4. Content Ownership</h3>
                                <div className="mb-2">
                                    All content - including logos, text, images, news, announcements, events, and materials - is the property of
                                    Northeastern College.
                                </div>
                                <div className="mb-2">You may not:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Reproduce or distribute content without permission</li>
                                    <li>Misrepresent or misuse official materials</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">5. Posting of Content</h3>
                                <div>Only authorized administrators and staff may create or publish content.</div>
                                <div>Users cannot post reviews, comments, or uploads.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">6. Third-Party Links</h3>
                                <div>
                                    The website may contain links to external sites. Northeastern College is not responsible for the content,
                                    policies, or actions of third-party websites.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">7. Disclaimer of Liability</h3>
                                <div className="mb-2">The website and its content are provided "as is".</div>
                                <div className="mb-2">We make no guarantees regarding:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Continuous availability</li>
                                    <li>Error-free operation</li>
                                    <li>Accuracy of external or third-party content</li>
                                </ul>
                                <div className="mt-3 mb-2">Northeastern College is not liable for any damages resulting from:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Use or inability to use the website</li>
                                    <li>Security incidents beyond our control</li>
                                    <li>Reliance on information posted on the site</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">8. Changes to the Website</h3>
                                <div>We may update or change website content, features, or policies at any time.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">9. Governing Law</h3>
                                <div>These Terms are governed by the laws of the Republic of the Philippines.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">10. Contact Information</h3>
                                <div>For inquiries regarding these Terms:</div>
                                <div className="mt-1">support@northeasterncollege.edu.ph</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
